/* eslint-disable react/destructuring-assignment */
import type { FC, ReactElement, RefObject } from "react"

import { motion as m } from "framer-motion"
import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import { useBoolean } from "react-use"

import { ArrowIcon } from "@/3514/assets"
import {
    Avatar,
    Button,
    EAvatarSize,
    EButtonVariant,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import {
    type TAnimatedSectionState,
    type TScreenBreakpoints,
    useAnimatedSection,
    useScreenBreakpoints
} from "@/3514/hooks"
import { type TCoachInfoSuspenseQuery, useCoachInfoSuspenseQuery } from "@/3514/queries"
import { EAppAnimationName } from "@/3514/store/app"
import { ERoundDirection, getCssIdentifierFromFileName, renderRichContent, withPressHandler } from "@/3514/utils"
import {
    type IUseTranslation,
    type TGetValueFromTranslatedObjectFunction,
    useGetValueFromTranslatedObject,
    useTranslation
} from "@/hooks"
import { richTextRendererUtils } from "@/shared/rich-text-renderer"

import { coachInfoConfig as config } from "./coach-info.config"
import { coachInfoStyles as classes } from "./coach-info.styles"
import {
    ECoachInfoIntegrationMode as EIntegrationMode,
    type TCoachInfoData as TData,
    type TCoachInfoEntityProps as TProps
} from "./coach-info.types"

const { getEditorInsertableClasses: getEditorClasses, editorAllowedTags } = config

const displayName: string = "CoachInfoEntity"
const bioEditorIdentifier: string = getCssIdentifierFromFileName(displayName, "b-ed")

type TEntityViewProps = {
    data: TData
    mode: EIntegrationMode
    titleConfig: Pick<TProps, "titleConfig">["titleConfig"]
    containerRef?: RefObject<HTMLDivElement>
    shouldAnimate?: boolean
    hasPlayed?: boolean
    isMotionReduced?: boolean
}

const EntityView: FC<TEntityViewProps> = ({
    data,
    mode,
    titleConfig,
    containerRef,
    shouldAnimate = true,
    hasPlayed = true,
    isMotionReduced = false
}: TEntityViewProps): ReactElement | null => {
    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const [isFullBioShown, setIsFullBioShown]: ReturnType<typeof useBoolean> = useBoolean(false)

    const [isEllipsisVisible, setIsEllipsisVisible]: ReturnType<typeof useBoolean> = useBoolean(true)

    const getValueFromTranslatedObjectFunction: TGetValueFromTranslatedObjectFunction =
        useGetValueFromTranslatedObject()

    if (isEmpty(data)) return null

    const bioToRender: string = getValueFromTranslatedObjectFunction(data.bio)

    const titleToRender: string =
        titleConfig.length === 1 ? t(titleConfig[0]) : t(titleConfig[0], { ...titleConfig[1] })

    return (
        <m.section
            ref={containerRef}
            className={classes.section({ mode })}
            initial={{ opacity: 0 }}
            animate={{ opacity: shouldAnimate || hasPlayed ? 1 : 0 }}
            transition={{ duration: isMotionReduced ? 0 : 0.6, ease: "easeOut" }}
            layout={false}
        >
            <div className={classes.leftCol({ mode })}>
                <Avatar
                    url={data.photo}
                    alt={data.firstName}
                    size={EAvatarSize._155}
                    userData={{ firstName: data.firstName, lastName: data.lastName }}
                />

                <div className={classes.leftColTextWrapper()}>
                    <Typography
                        className={classes.leftColName()}
                        alignment={ETypographyAlignment.Center}
                        family={ETypographyFamily.DmSerif}
                        size={ETypographySize.Medium}
                        weight={ETypographyWeight.Medium}
                        color={ETypographyColor.Black}
                        text={`${data.firstName} ${data.lastName}`}
                    />

                    {data.certification && (
                        <Typography
                            alignment={ETypographyAlignment.Center}
                            text={data.certification}
                            tag={ETypographyTag.Paragraph}
                            color={ETypographyColor.Dark}
                            size={ETypographySize.Small}
                        />
                    )}

                    {Boolean(!isEmpty(data.country) || !isEmpty(data.location)) && (
                        <Typography
                            alignment={ETypographyAlignment.Center}
                            tag={ETypographyTag.Paragraph}
                            text={`${data.country}${data.country && data.location ? ", " : ""}${data.location}`}
                            color={ETypographyColor.Dark}
                            size={ETypographySize.Small}
                        />
                    )}
                </div>

                {eq(mode, EIntegrationMode.OnboardingCoach) && (
                    <div data-index={data.id}>
                        <Button isCentered variant={EButtonVariant.Solid} minWidth="150px">
                            {t("common.schedule")}
                        </Button>
                    </div>
                )}
            </div>

            {!isEmpty(bioToRender) && (
                <div className={classes.rightCol()}>
                    <Typography
                        text={titleToRender}
                        family={ETypographyFamily.DmSerif}
                        color={ETypographyColor.Black}
                        weight={ETypographyWeight.Medium}
                        size={ETypographySize.Heading2}
                        tag={ETypographyTag.Heading2}
                    />

                    <div>
                        <m.div
                            initial={false}
                            animate={{
                                height:
                                    isFullBioShown || bioToRender.length < 600
                                        ? "auto"
                                        : breakpoints.xs || breakpoints.sm || breakpoints.md
                                          ? 150
                                          : 210
                            }}
                            transition={{
                                duration: isMotionReduced ? 0 : 0.25,
                                type: "keyframes",
                                ease: "circInOut"
                            }}
                            onAnimationStart={setIsEllipsisVisible}
                            onAnimationComplete={setIsEllipsisVisible}
                            className={classes.rightColBioWrapper()}
                        >
                            {richTextRendererUtils.isStringPlainText(bioToRender) ? (
                                <Typography
                                    text={bioToRender}
                                    tag={ETypographyTag.Paragraph}
                                    size={ETypographySize.Small}
                                    color={ETypographyColor.Black}
                                    className={classes.rightColBio()}
                                />
                            ) : (
                                renderRichContent({
                                    allowedTags: editorAllowedTags,
                                    identifier: bioEditorIdentifier,
                                    classes: getEditorClasses(bioEditorIdentifier),
                                    editorClasses: classes.rightColBio()
                                })(bioToRender)
                            )}

                            {isEllipsisVisible && bioToRender.length > 600 && (
                                <div
                                    className={classes.rightColBioContainerShadow({
                                        isContainerExpanded: isFullBioShown
                                    })}
                                />
                            )}
                        </m.div>

                        {bioToRender.length > 600 && (
                            <div
                                aria-label={t("common.showMore")}
                                className={classes.rightColBioToggler()}
                                tabIndex={0}
                                role="button"
                                onClick={withPressHandler(setIsFullBioShown)}
                                onKeyDown={withPressHandler(setIsFullBioShown)}
                            >
                                <Typography
                                    text={isFullBioShown ? t("common.showLess") : t("common.showMore")}
                                    tag={ETypographyTag.Paragraph}
                                    color={ETypographyColor.Accent}
                                    size={ETypographySize.Small}
                                    className={classes.rightColBioTogglerText()}
                                />

                                <ArrowIcon
                                    width={16}
                                    height={16}
                                    direction={isFullBioShown ? ERoundDirection.Top : ERoundDirection.Bottom}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </m.section>
    )
}
EntityView.displayName = "CoachInfoEntityView"

type TSessionsEntityProps = { titleConfig: Pick<TProps, "titleConfig">["titleConfig"]; participantId: number }
const SessionsEntity: FC<TSessionsEntityProps> = (props: TSessionsEntityProps): ReactElement => {
    const { data }: TCoachInfoSuspenseQuery = useCoachInfoSuspenseQuery({ participantId: props.participantId })

    const { shouldAnimate, containerRef, hasPlayed, isMotionReduced }: TAnimatedSectionState = useAnimatedSection({
        animationName: EAppAnimationName.Participant_CoachingSessions_Coach
    })

    return isEmpty(data) ? null : (
        <EntityView
            data={{
                bio: data.bio,
                id: data.id,
                firstName: data.firstName,
                lastName: data.lastName,
                photo: data.photo,
                certification: data.certification,
                country: data.country,
                location: data.location
            }}
            {...props}
            mode={EIntegrationMode.CoachingSessions}
            containerRef={containerRef}
            shouldAnimate={shouldAnimate}
            hasPlayed={hasPlayed}
            isMotionReduced={isMotionReduced}
        />
    )
}
SessionsEntity.displayName = "CoachInfoSessionsEntity"

type TOnboardingEntityProps = { coachData: TData; titleConfig: Pick<TProps, "titleConfig">["titleConfig"] }
const OnboardingEntity: FC<TOnboardingEntityProps> = ({
    coachData,
    ...restProps
}: TOnboardingEntityProps): ReactElement => (
    <EntityView mode={EIntegrationMode.OnboardingCoach} data={coachData} {...restProps} />
)
OnboardingEntity.displayName = "CoachInfoOnboardingEntity"

function Entity(props: TProps): ReactElement {
    return props.mode === EIntegrationMode.OnboardingCoach ? (
        <OnboardingEntity coachData={props.coachData} titleConfig={props.titleConfig} />
    ) : (
        <SessionsEntity titleConfig={props.titleConfig} participantId={props.participantId} />
    )
}

Entity.displayName = displayName

export { Entity as CoachInfoEntity }
